/**
 * Pinia-стор терминала, авторизации и профиля.
 * Сохраняет поведение корневого Vuex-стора (state + mutations + actions).
 */
import { defineStore } from 'pinia'
import { api } from '@/api'
import { apiGet, apiPost } from '@/api/client'
import { bonusesEnabled } from '@/config/bonuses'
import { productProfile } from '@/config/productProfile'
import { useCartStore } from '@/stores/cart'
import type { BonusData, Profile, TerminalData } from '@/types'

export const useTerminalStore = defineStore('terminal', {
  state: () => ({
    terminalData: {
      cafe_slug: null,
      shop_id: null,
      terminal_id: null,
      terminal_type: 't-bank',
    } as TerminalData,

    pick_up: 'pickup_on_tray' as string,
    bonusesEnabled,
    bonusesUnavailableShow: false,
    authShow: false,
    authBanner: productProfile.authBanner,
    phone: null as string | null,
    token: null as string | null,
    profile: null as (Profile & Partial<BonusData>) | null,
    orderStartAt: null as string | null,
    comboQuestionCounter: 2,
    // ВРЕМЕННАЯ фича: отключает показ combo-модалки при клике на товар
    disableComboModal: true,
  }),

  getters: {
    isAuthenticated: (state) => Boolean(state.token),
  },

  actions: {
    setTerminal(data: TerminalData) {
      this.terminalData = data
    },

    setPickUp(pick_up: string) {
      this.pick_up = pick_up;
    },

    setAuth(bool: boolean) {
      if (bool && !this.bonusesEnabled) {
        this.authShow = false
        this.bonusesUnavailableShow = true
        return
      }
      this.authShow = bool;
    },

    setAuthBanner(bool: boolean) {
      this.authBanner = bool
    },

    setPhone(phone: string | null) {
      this.phone = phone;
    },

    setToken(token: string | null) {
      this.token = token;
    },

    setProfile(data: { profile?: Profile } | { bonuses?: BonusData } | null) {
      if (data) {
        if ('profile' in data && data.profile) {
          this.profile = data.profile;
        } else if ('bonuses' in data && data.bonuses) {
          Object.assign(this.profile ?? {}, data.bonuses);
        }
      } else {
        this.profile = null
      }
    },

    setOrderStart(dateTime: string) {
      this.orderStartAt = dateTime
    },

    setComboQuestionCounter() {
      this.comboQuestionCounter--
    },

    setDisableComboModal(val: boolean) {
      this.disableComboModal = val
    },

    resetState() {
      this.authShow = false;
      this.bonusesUnavailableShow = false;
      this.phone = null;
      this.token = null;
      this.profile = null;
      this.orderStartAt = null
      const cartStore = useCartStore()
      cartStore.cart = [];
      cartStore.promocode = null;
      cartStore.promoObj = null;
      this.comboQuestionCounter = 2
    },

    async getShops() {
      return apiGet<{ results: unknown[] }>(`${api}/shops/`, { skipLogging: true });
    },

    async sendAuthPhone(data: { phone: string; source: string; shop: string | null }, signal?: AbortSignal) {
      return apiPost<{ token?: string; success?: boolean; error?: string; message?: string }>(`${api}/auth/phone-v2/`, data, { skipLogging: true, signal, timeout: 35_000 });
    },

    async sendAuthCode(data: { phone: string; code: string }, signal?: AbortSignal) {
      const result = await apiPost<{ token?: string; message?: string }>(`${api}/auth/verification/`, {
        phone: data.phone,
        code: data.code,
      }, { signal, timeout: 35_000 })
      if (result.token && !signal?.aborted) {
        this.setToken(result.token);
      }
      return result;
    },

    async sendAuthMobileId(data: { session_id: string; verify_token: string }, signal?: AbortSignal) {
      const result = await apiPost<{ token?: string }>(`${api}/auth/verification/`, {
        session_id: data.session_id,
        verify_token: data.verify_token,
        source: 'app',
      }, { skipLogging: true, signal, timeout: 35_000 })
      if (!result.token) {
        throw new Error('Mobile ID verification returned no token')
      }
      if (!signal?.aborted) {
        this.setToken(result.token)
      }
      return result
    },

    async getProfile(signal?: AbortSignal) {
      const currentToken = this.token
      const data = await apiGet<Profile>(`${api}/profile/`, { skipLogging: true, signal, timeout: 35_000 })
      if (data && !signal?.aborted && this.token === currentToken) {
        this.setProfile({ profile: data });
        await this.getBonuses(signal)
        if (signal?.aborted || this.token !== currentToken) { return null }
        return data
      }
      return null;
    },

    async setProfileData(name: string, signal?: AbortSignal) {
      if (this.token) {
        const currentToken = this.token
        try {
          const data = await apiPost<{ user?: Profile; first_name?: string; message?: string }>(`${api}/profile/`, { first_name: name }, { skipLogging: true, signal, timeout: 35_000 })
          if (data.user && !signal?.aborted && this.token === currentToken) {
            this.setProfile({ profile: data.user })
            await this.getBonuses(signal)
          }
          return data;
        } catch (error) {
          return (error as { response?: { data: unknown } }).response?.data
        }
      } else {
        alert('Ошибка! Отсутствует токен авторизации.');
      }
    },

    async getBonuses(signal?: AbortSignal) {
      const currentToken = this.token
      const data = await apiGet<BonusData>(`${api}/bonuses/`, { skipLogging: true, signal, timeout: 35_000 })
      if (data && !signal?.aborted && this.token === currentToken) {
        this.setProfile({ bonuses: data });
      }
      return data;
    },

    async logout() {
      this.setToken(null);
      this.setProfile(null);
      this.setPhone(null);
    },
  },
})
